import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from "../../context/AuthContext";

import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Avatar,
  TextField,
  InputAdornment,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Stack
} from '@mui/material';

import {
  Visibility,
  WhatsApp,
  Inventory2,
  Add,
  Palette,
  Link as LinkIcon,
  OpenInNew,
  HelpOutline,
  ShoppingBag,
  AttachMoney,
  CheckCircle,
  Store,
  ContentCopy
} from '@mui/icons-material';

// --- Componente: Card de Estatística ---
const StatCard = ({ title, value, subValue, icon, iconBg }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      border: '1px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}
  >
    <Box>
      <Typography variant="body2" color="text.secondary" fontWeight="500">
        {title}
      </Typography>
      <Typography variant="h5" fontWeight="bold" sx={{ my: 0.5 }}>
        {value}
      </Typography>
      {subValue && (
        <Typography variant="caption" color="text.secondary">
          {subValue}
        </Typography>
      )}
    </Box>
    <Avatar
      sx={{
        bgcolor: iconBg,
        width: 44,
        height: 44,
        borderRadius: 2
      }}
    >
      {icon}
    </Avatar>
  </Paper>
);

// --- Componente: Ação Rápida ---
const QuickAction = ({ icon, label, onClick }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 3,
      border: '1px solid #e0e0e0',
      textAlign: 'center',
      cursor: 'pointer',
      flex: '1 1 120px',
      minWidth: 100,
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: '#7c4dff',
        bgcolor: '#fcfaff'
      }
    }}
  >
    <Box sx={{ color: '#7c4dff', mb: 0.5 }}>{icon}</Box>
    <Typography variant="caption" fontWeight="600" display="block">
      {label}
    </Typography>
  </Paper>
);

// --- Componente Principal da Dashboard ---
export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isStoreOpen, setIsStoreOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  const storeDomain = user?.store_domain || 'minhaloja';
  const storeUrl = `https://${storeDomain}.fazpedido.app/`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(storeUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Exemplo de dados de pedidos recentes
  const recentOrders = [
    { id: '#1024', client: 'João Silva', total: 'R$ 45,90', status: 'Novo', time: 'Há 5 min' },
    { id: '#1023', client: 'Maria Oliveira', total: 'R$ 89,00', status: 'Em preparo', time: 'Há 18 min' },
    { id: '#1022', client: 'Carlos Souza', total: 'R$ 32,50', status: 'Entregue', time: 'Há 42 min' }
  ];

  return (
    <Box sx={{ width: '100%', minHeight: '100vh' }}>

      {/* Cabeçalho com Status da Loja */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          mb: 3,
          border: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Store sx={{ color: '#7c4dff', fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {user?.store_name || 'Minha Loja'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Status da loja no catálogo online
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight="600">
            {isStoreOpen ? 'Loja Aberta' : 'Loja Fechada'}
          </Typography>
          <Switch
            checked={isStoreOpen}
            onChange={(e) => setIsStoreOpen(e.target.checked)}
            color="success"
          />
        </Stack>
      </Paper>

      <Grid container spacing={3}>

        {/* Conteúdo Principal */}
        <Grid item size={{ xs: 12, md: 8 }}>

          {/* Métricas e Métricas operacionais */}


          {/* Ações Rápidas */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              mb: 3,
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Ações Rápidas
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <QuickAction
                icon={<Add />}
                label="Novo Produto"
                onClick={() => navigate('/produtos/novo')}
              />
              <QuickAction
                icon={<Palette />}
                label="Personalizar"
                onClick={() => navigate('/appearance')}
              />
              <QuickAction
                icon={<LinkIcon />}
                label="Config. Link"
                onClick={() => navigate('/link')}
              />
              <QuickAction
                icon={<Inventory2 />}
                label="Estoque"
                onClick={() => navigate('/produtos')}
              />
              <QuickAction
                icon={<OpenInNew />}
                label="Ver Catálogo"
                onClick={() => window.open(storeUrl, '_blank')}
              />
            </Box>
          </Paper>


        </Grid>

        {/* Sidebar Lateral */}
        <Grid item size={{ xs: 12, md: 4 }}>

          {/* Card: Compartilhar Loja */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid #e0e0e0',
              mb: 3
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Divulgue seu Catálogo
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Copie o link da sua loja para colocar na bio do Instagram ou enviar aos clientes.
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={storeUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={handleCopyLink}
                      startIcon={copied ? <CheckCircle color="success" /> : <ContentCopy />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: copied ? 'green' : '#7c4dff'
                      }}
                    >
                      {copied ? 'Copiado!' : 'Copiar'}
                    </Button>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  bgcolor: '#f8f9fa'
                }
              }}
            />
          </Paper>

          {/* Card: Configuração da Loja (Progresso) */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid #e0e0e0',
              mb: 3
            }}
          >
            <Chip
              label="PASSO A PASSO"
              size="small"
              sx={{ bgcolor: '#7c4dff', color: 'white', fontWeight: 'bold', mb: 1.5 }}
            />

            <Typography variant="h6" fontWeight="bold">
              67% concluído
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Próximo passo: Adicionar formas de pagamento
            </Typography>

            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => navigate('/settings')}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                bgcolor: '#7c4dff',
                '&:hover': { bgcolor: '#651fff' }
              }}
            >
              Continuar Configuração
            </Button>
          </Paper>

          {/* Suporte e Ajuda */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Avatar sx={{ bgcolor: '#f3eaff' }}>
                <HelpOutline sx={{ color: '#7c4dff' }} />
              </Avatar>

              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Precisa de Ajuda?
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ my: 0.5 }}>
                  Fale com nosso time via WhatsApp ou veja os tutoriais.
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => navigate('/support')}
                  sx={{
                    color: '#7c4dff',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Central de Ajuda →
                </Typography>
              </Box>
            </Box>
          </Paper>

        </Grid>

      </Grid>
    </Box>
  );
}